import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Activity, TrendingDown, TrendingUp, Info } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL;

export default function MoodSymptomCorrelation() {
    const { language } = useLanguage();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [days, setDays] = useState(90);

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            try {
                const res = await axios.get(`${API}/api/mood/mood-symptom-correlation?days=${days}`);
                setData(res.data);
            } catch (e) { console.error('Correlation load error:', e); }
            finally { setLoading(false); }
        };
        load();
    }, [days]);

    const t = (de, en) => (language === 'de' ? de : en);

    if (loading) {
        return (
            <Card className="animate-pulse" data-testid="mood-symptom-correlation-loading">
                <CardContent className="h-32" />
            </Card>
        );
    }
    if (!data) return null;

    const { avg_mood_with_symptoms, avg_mood_without_symptoms, delta, days_with_symptoms, period_days } = data;
    const hasData = avg_mood_with_symptoms != null && avg_mood_without_symptoms != null;

    // delta = without - with. Positive delta means feeling better on symptom-free days.
    const deltaPositive = delta != null && delta > 0;
    const deltaColor = deltaPositive ? 'text-emerald-500' : delta != null && delta < 0 ? 'text-rose-500' : 'text-muted-foreground';
    const TrendIcon = deltaPositive ? TrendingUp : TrendingDown;

    return (
        <Card className="border bg-card" data-testid="mood-symptom-correlation">
            <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                        <Activity className="w-4 h-4 text-violet-400" />
                        <h3 className="text-sm font-semibold">
                            {t('Stimmung ↔ Symptome', 'Mood ↔ Symptoms')}
                        </h3>
                    </div>
                    <div className="flex gap-1">
                        {[30, 90, 180].map((d) => (
                            <button
                                key={d}
                                onClick={() => setDays(d)}
                                className={`text-[10px] px-2 py-0.5 rounded-md border transition-colors ${
                                    days === d
                                        ? 'bg-primary text-primary-foreground border-primary'
                                        : 'border-border text-muted-foreground hover:bg-muted'
                                }`}
                                data-testid={`mood-corr-range-${d}`}
                            >
                                {d}T
                            </button>
                        ))}
                    </div>
                </div>

                {!hasData ? (
                    <div className="flex items-start gap-2 text-xs text-muted-foreground bg-muted/30 rounded-lg p-3">
                        <Info className="w-4 h-4 shrink-0 mt-0.5" />
                        <p>
                            {t(
                                `Noch nicht genug Daten in den letzten ${period_days} Tagen. Logge regelmäßig Stimmung und Symptome, um Zusammenhänge zu erkennen.`,
                                `Not enough data in the last ${period_days} days. Log mood and symptoms regularly to spot patterns.`
                            )}
                        </p>
                    </div>
                ) : (
                    <>
                        <div className="grid grid-cols-2 gap-2 mb-3">
                            <div className="p-3 rounded-xl bg-rose-500/10 border border-rose-500/20" data-testid="mood-with-symptoms">
                                <p className="text-[10px] text-muted-foreground mb-1">
                                    {t('An Symptom-Tagen', 'On symptom days')}
                                </p>
                                <p className="text-xl font-bold text-rose-400">
                                    Ø {avg_mood_with_symptoms?.toFixed(1)}
                                    <span className="text-xs font-normal text-muted-foreground ml-1">/5</span>
                                </p>
                            </div>
                            <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20" data-testid="mood-without-symptoms">
                                <p className="text-[10px] text-muted-foreground mb-1">
                                    {t('An beschwerdefreien Tagen', 'On symptom-free days')}
                                </p>
                                <p className="text-xl font-bold text-emerald-400">
                                    Ø {avg_mood_without_symptoms?.toFixed(1)}
                                    <span className="text-xs font-normal text-muted-foreground ml-1">/5</span>
                                </p>
                            </div>
                        </div>

                        {delta != null && (
                            <div className={`flex items-center gap-2 text-sm font-medium ${deltaColor}`} data-testid="mood-corr-delta">
                                <TrendIcon className="w-4 h-4" />
                                <span>
                                    Δ {delta > 0 ? '+' : ''}{delta.toFixed(1)}
                                </span>
                                <span className="text-xs text-muted-foreground font-normal">
                                    {deltaPositive
                                        ? t('besser ohne Symptome', 'better without symptoms')
                                        : delta < 0
                                            ? t('besser mit Symptomen (untypisch)', 'better with symptoms (atypical)')
                                            : t('kein Unterschied', 'no difference')}
                                </span>
                            </div>
                        )}

                        <p className="text-[10px] text-muted-foreground mt-3 leading-relaxed">
                            {t(
                                `Basierend auf ${days_with_symptoms} Symptom-Tagen in ${period_days} Tagen.`,
                                `Based on ${days_with_symptoms} symptom days in ${period_days} days.`
                            )}
                        </p>
                    </>
                )}
            </CardContent>
        </Card>
    );
}
